import * as spacesApi from "@/api/spaces";
import { applyFramePatch } from "@/lib/frame-ingest";
import { rememberLocalFrameGeometry } from "@/stores/frame-geometry-guard";
import { useProjectStore } from "@/stores/project-store";

type FrameUpdate = Parameters<typeof spacesApi.updateFrame>[3];
type PersistedFrame = Awaited<ReturnType<typeof spacesApi.updateFrame>>;

const persistenceTails = new Map<string, Promise<void>>();

function persistenceKey(
  projectId: string,
  spaceId: string,
  frameId: string,
): string {
  return `${projectId}\0${spaceId}\0${frameId}`;
}

/**
 * Serializes geometry PATCHes per frame so a later reset cannot be overtaken
 * by an earlier resize request that finishes after it.
 */
export function persistFrameGeometry(
  projectId: string,
  spaceId: string,
  frameId: string,
  update: FrameUpdate,
): Promise<PersistedFrame> {
  const key = persistenceKey(projectId, spaceId, frameId);
  const previous = persistenceTails.get(key);
  const request = previous
    ? previous.then(() =>
        spacesApi.updateFrame(projectId, spaceId, frameId, update),
      )
    : spacesApi.updateFrame(projectId, spaceId, frameId, update);
  const tail = request.then(
    () => undefined,
    () => undefined,
  );
  persistenceTails.set(key, tail);
  void tail.then(() => {
    if (persistenceTails.get(key) === tail) persistenceTails.delete(key);
  });
  return request;
}

export interface FrameGeometryUpdate {
  id: string;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Persist placed frames in the current space. Local geometry is remembered
 * first, so an older server echo cannot move a frame back, and the stored
 * frame is applied once a geometry PATCH lands.
 */
export function persistFrameGeometryUpdates(
  updates: readonly FrameGeometryUpdate[],
): void {
  const projectId = useProjectStore.getState().currentProject?.id;
  const spaceId = useProjectStore.getState().currentSpace?.id;
  if (!projectId || !spaceId) return;
  for (const update of updates) {
    const hasGeometry =
      update.position_x !== undefined ||
      update.position_y !== undefined ||
      update.width !== undefined ||
      update.height !== undefined;
    rememberLocalFrameGeometry(update.id, {
      ...(update.position_x !== undefined && {
        position_x: update.position_x,
      }),
      ...(update.position_y !== undefined && {
        position_y: update.position_y,
      }),
      ...(update.width !== undefined && { width: update.width }),
      ...(update.height !== undefined && { height: update.height }),
    });
    void persistFrameGeometry(projectId, spaceId, update.id, {
      ...(update.position_x !== undefined && {
        position_x: update.position_x,
      }),
      ...(update.position_y !== undefined && {
        position_y: update.position_y,
      }),
      ...(update.width !== undefined && { width: update.width }),
      ...(update.height !== undefined && { height: update.height }),
      ...(update.metadata && { metadata: update.metadata }),
    })
      .then((frame) => {
        if (!hasGeometry) return;
        applyFramePatch(update.id, frame, { origin: "server" });
      })
      .catch(() => {});
  }
}
